import axios from "axios";
import {
    fileUploadCompleted,
    fileUploadStarted,
    fileUploadProgress,
    fileUploadFailed,
    getFileFailed,
    fileUploadCleared,
    setNavigationEnabled,
} from "../actions";
import { StoreState } from "../../models/states/StoreState";
import { SuccessModel } from "../../models/SuccessModel";
import { ThunkActionType } from "../actions/types";
import { makeAxios } from "./validation";
import { FileUploadErrorModel } from "../../models/FileUploadErrorModel";
import { AxiosCancellationTokenSource } from "../../utils/AxiosCancellationTokenSource";

const enableNavigation: ThunkActionType = (dispatch, getState) => {
    const state = getState();
    if (
        Object.values(state.files.filesByVariableName).every((v) =>
            v.every((f) => f.status === "completed" || f.status === "failed"),
        )
    ) {
        dispatch(setNavigationEnabled(true));
    }
};

export function uploadFile(
    variableName: string,
    matrix: number,
    file: File,
    callback: (serverErrorKey?: string) => void,
): ThunkActionType<Promise<void>> {
    return async (dispatch, getState) => {
        const state: StoreState = getState();
        if (state.interview.respondent.simulated) {
            dispatch(fileUploadCompleted(variableName, matrix));
            callback();
            return;
        }
        const data = new FormData();
        data.append("variableName", variableName);
        data.append("matrix", String(matrix));
        data.append("file", file);

        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        const cancellationTokenSource = new AxiosCancellationTokenSource(source);

        dispatch(
            fileUploadStarted(variableName, matrix, file.name, file.size, cancellationTokenSource),
        );
        dispatch(setNavigationEnabled(false));
        try {
            const result = await dispatch(makeAxios()).post<FileUploadErrorModel>(
                "f/Upload",
                data,
                {
                    cancelToken: source.token,
                    onUploadProgress: (pe: ProgressEvent) => {
                        if (pe.lengthComputable)
                            dispatch(
                                fileUploadProgress(variableName, matrix, pe.loaded / pe.total),
                            );
                    },
                },
            );
            if (result.status !== 200) throw new Error(`Unexpected status ${result.status}`);
            const { success, key } = result.data;
            if (!success) throw new Error(`File upload failed ${file.name}`);
            if (key) {
                dispatch(
                    fileUploadFailed(
                        variableName,
                        matrix,
                        file.name,
                        new Error(`File upload failed ${file.name}`),
                    ),
                );
                callback(key);
                return;
            }
            dispatch(fileUploadCompleted(variableName, matrix));
            callback();
        } catch (err) {
            if (axios.isCancel(err)) return;
            dispatch(fileUploadFailed(variableName, matrix, file.name, err));
            const { response } = err;
            if (response && response.status === 413) {
                callback("InvalidFileSize");
                return;
            }
            callback("UnexpectedError");
        } finally {
            dispatch(enableNavigation);
        }
    };
}

export function getFile(
    variableName: string,
    fileName: string,
    mimType: string,
    matrix = 0,
): ThunkActionType<Promise<any>> {
    return async (dispatch, getState) => {
        try {
            const state: StoreState = getState();
            if (state.interview.respondent.simulated) return null;
            const result = await dispatch(makeAxios()).get("f/GetFile", {
                params: { variableName, fileName, matrix },
                responseType: "blob",
            });
            if (result.status !== 200) throw new Error(`Unexpected status ${result.status}`);
            if (result.data.type !== mimType) throw new Error(`File not found ${fileName}`);
            return result.data;
        } catch (err) {
            dispatch(getFileFailed(variableName, fileName, err));
            return null;
        }
    };
}

export function clearFile(variableName: string, matrix: number): ThunkActionType<Promise<void>> {
    return async (dispatch, getState) => {
        const state: StoreState = getState();
        if (state.interview.respondent.simulated) return;
        dispatch(fileUploadCleared(variableName, matrix));
        dispatch(enableNavigation);
        try {
            const result = await dispatch(makeAxios()).delete<SuccessModel>("f/delete", {
                params: { variableName, matrix },
            });
            if (result.status !== 200) throw new Error(`Unexpected status ${result.status}`);
            if (!result.data.success) throw new Error(`File deletion failed ${variableName}`);
        } catch (err) {
            console.log(err);
        }
    };
}
